import { StandardEvents } from '@shopify/events';

const CONTRACT_VERSION = '1.3.0';
const CONTRACT_EVENT = 'pokegeek:cart-contract-updated';
const INITIAL_DATA_ID = 'pokegeek-cart-contract-data';
const DEBUG_DATA_ID = 'pokegeek-cart-contract-current';
const GIFT_TIERS = Object.freeze([
  Object.freeze({ id: 'bonus', threshold: 4900 }),
  Object.freeze({ id: 'premium', threshold: 7900 }),
  Object.freeze({ id: 'collector', threshold: 10900 }),
]);
const GIFT_TIER_IDS = Object.freeze(['bonus', 'premium', 'collector']);
const DEFAULT_GIFT_THRESHOLDS = Object.freeze({
  EUR: Object.freeze({ bonus: 4900, premium: 7900, collector: 10900 }),
});
const PROTECTION_RATE_BASIS_POINTS = 200;
const PROTECTION_MINIMUM_PRICE = 179;
const PROTECTION_INCREMENT = 79;
const PROTECTION_TIER_COUNT = 99;
const PROTECTION_MAXIMUM_PRICE = 7921;
const PROTECTION_MAX_ELIGIBLE_SUBTOTAL = 396050;
const PROTECTION_LINE_PROPERTY = '_pg_shipping_protection';
const PROTECTION_SKU_PREFIX = 'PG-PROT-';

const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
]);
const THREE_DECIMAL_CURRENCIES = new Set(['BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND']);

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function integerOrNull(value) {
  if (typeof value === 'number') return Number.isSafeInteger(value) ? value : null;
  if (typeof value !== 'string' || !/^-?\d+$/.test(value.trim())) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function nonNegativeInteger(value, fallback = 0) {
  const parsed = integerOrNull(value);
  return parsed !== null && parsed >= 0 ? parsed : fallback;
}

function protectionQuoteResult(eligibleSubtotal, supported, tierIndex, quotedPrice, reason) {
  return Object.freeze({
    supported,
    threshold_currency: 'EUR',
    eligible_subtotal: eligibleSubtotal,
    rate_basis_points: PROTECTION_RATE_BASIS_POINTS,
    minimum_price: PROTECTION_MINIMUM_PRICE,
    increment: PROTECTION_INCREMENT,
    maximum_price: PROTECTION_MAXIMUM_PRICE,
    tier_index: tierIndex,
    quoted_price: quotedPrice,
    reason,
  });
}

function normalizeGiftThresholds(input) {
  const source = isObject(input) ? input : {};
  const normalized = {};

  for (const [currency, values] of Object.entries(source)) {
    if (!isObject(values)) continue;
    const code = String(currency || '').trim().toUpperCase();
    const bonus = integerOrNull(values.bonus);
    const premium = integerOrNull(values.premium);
    const collector = integerOrNull(values.collector);
    if (!code || bonus === null || premium === null || collector === null) continue;
    if (bonus < 0 || premium <= bonus || collector <= premium) continue;
    normalized[code] = Object.freeze({ bonus, premium, collector });
  }

  return Object.freeze(normalized);
}

function resolvedGiftThresholds(input) {
  if (input === null || input === undefined) return DEFAULT_GIFT_THRESHOLDS;
  return normalizeGiftThresholds(input);
}

function normalizeGiftVariantIdsByTier(input) {
  const source = isObject(input) ? input : {};
  return Object.freeze(Object.fromEntries(
    GIFT_TIER_IDS.map((tier) => [tier, normalizeId(source[tier])])
  ));
}

function giftStateResult(supported, reason, tier, threshold, remaining, expectedVariantId) {
  return Object.freeze({
    supported,
    reason,
    tier,
    threshold,
    remaining,
    expected_variant_id: expectedVariantId,
  });
}

function calculateGiftState(input) {
  const source = isObject(input) ? input : {};
  const subtotal = integerOrNull(source.gift_eligible_subtotal);
  const currency = typeof source.currency === 'string' ? source.currency.trim().toUpperCase() : '';
  const thresholdsByCurrency = resolvedGiftThresholds(source.gift_thresholds);
  const thresholds = thresholdsByCurrency[currency] || null;
  const variants = normalizeGiftVariantIdsByTier(source.gift_variant_ids_by_tier);

  if (subtotal === null || subtotal < 0 || !currency) {
    return giftStateResult(false, 'invalid_input', null, null, null, null);
  }
  if (!thresholds) {
    return giftStateResult(false, 'unsupported_currency', null, null, null, null);
  }

  let tier = null;
  if (subtotal >= thresholds.collector) tier = 'collector';
  else if (subtotal >= thresholds.premium) tier = 'premium';
  else if (subtotal >= thresholds.bonus) tier = 'bonus';

  if (tier === null) {
    return giftStateResult(true, 'below_bonus', null, thresholds.bonus, thresholds.bonus - subtotal, null);
  }

  const nextTier = tier === 'bonus' ? 'premium' : tier === 'premium' ? 'collector' : null;
  const remaining = nextTier ? Math.max(0, thresholds[nextTier] - subtotal) : 0;
  return giftStateResult(true, 'eligible', tier, thresholds[tier], remaining, variants[tier]);
}

function calculateProtectionQuote(input) {
  const source = isObject(input) ? input : {};
  const eligibleSubtotal = integerOrNull(source.protection_eligible_subtotal);
  const currency = typeof source.currency === 'string' && source.currency.trim() !== ''
    ? source.currency.trim().toUpperCase()
    : null;

  if (eligibleSubtotal === null || eligibleSubtotal < 0 || currency === null) {
    return protectionQuoteResult(null, false, null, null, 'invalid_input');
  }
  if (eligibleSubtotal === 0) {
    return protectionQuoteResult(0, false, null, null, 'empty_eligible_cart');
  }
  if (currency !== 'EUR') {
    return protectionQuoteResult(eligibleSubtotal, false, null, null, 'unsupported_currency');
  }
  if (eligibleSubtotal > PROTECTION_MAX_ELIGIBLE_SUBTOTAL) {
    return protectionQuoteResult(eligibleSubtotal, false, null, null, 'above_supported_range');
  }

  const rateComparisonValue = eligibleSubtotal * 2;
  for (let tierIndex = 0; tierIndex < PROTECTION_TIER_COUNT; tierIndex += 1) {
    const tierPrice = PROTECTION_MINIMUM_PRICE + PROTECTION_INCREMENT * tierIndex;
    if (tierPrice * 100 >= rateComparisonValue) {
      return protectionQuoteResult(eligibleSubtotal, true, tierIndex, tierPrice, 'eligible');
    }
  }

  return protectionQuoteResult(eligibleSubtotal, false, null, null, 'above_supported_range');
}

function currencyFractionDigits(currency) {
  const code = String(currency || '').toUpperCase();
  if (ZERO_DECIMAL_CURRENCIES.has(code)) return 0;
  if (THREE_DECIMAL_CURRENCIES.has(code)) return 3;
  return 2;
}

function decimalMoneyStringToMinorUnits(value, currency) {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const text = String(value).trim();
  const match = text.match(/^(-?)(\d+)(?:\.(\d+))?$/);
  if (!match) return null;

  const digits = currencyFractionDigits(currency);
  const fraction = match[3] || '';
  if (fraction.length > digits && /[1-9]/.test(fraction.slice(digits))) return null;

  const paddedFraction = `${fraction}${'0'.repeat(digits)}`.slice(0, digits);
  const scale = 10 ** digits;
  const major = integerOrNull(match[2]);
  const minor = paddedFraction === '' ? 0 : integerOrNull(paddedFraction);
  if (major === null || minor === null) return null;

  const result = major * scale + minor;
  if (!Number.isSafeInteger(result)) return null;
  return match[1] === '-' ? -result : result;
}

function normalizeId(value) {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
}

function normalizeProperties(properties) {
  if (!isObject(properties)) return Object.freeze({});
  const normalized = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === null || ['string', 'number', 'boolean'].includes(typeof value)) normalized[String(key)] = value;
  }
  return Object.freeze(normalized);
}

function normalizeAllocations(item) {
  const source = Array.isArray(item?.line_level_discount_allocations)
    ? item.line_level_discount_allocations
    : Array.isArray(item?.discount_allocations)
      ? item.discount_allocations
      : [];

  return source.map((allocation) => {
    const amount = nonNegativeInteger(allocation?.amount, 0);
    const application = allocation?.discount_application || allocation?.discountApplication || null;
    return Object.freeze({
      amount,
      title: typeof application?.title === 'string' ? application.title : null,
      type: typeof application?.type === 'string' ? application.type : null,
    });
  });
}

function sumIntegers(values) {
  let total = 0;
  for (const value of values) {
    const integer = integerOrNull(value);
    if (integer === null) continue;
    total += integer;
    if (!Number.isSafeInteger(total)) throw new RangeError('Cart monetary total exceeds the safe integer range');
  }
  return total;
}

function unitPriceFromLine(linePrice, quantity) {
  if (!Number.isSafeInteger(linePrice) || !Number.isSafeInteger(quantity) || quantity <= 0) return null;
  if (linePrice % quantity !== 0) return null;
  return linePrice / quantity;
}

function multiplyMoney(unitPrice, quantity) {
  if (!Number.isSafeInteger(unitPrice) || !Number.isSafeInteger(quantity)) return null;
  const result = unitPrice * quantity;
  return Number.isSafeInteger(result) ? result : null;
}

function normalizeTrustedIdSet(values) {
  return new Set(Array.isArray(values) ? values.map(normalizeId).filter(Boolean) : []);
}

function normalizeTrustedStringSet(values, { lowercase = false } = {}) {
  return new Set(
    (Array.isArray(values) ? values : [])
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .map((value) => lowercase ? value.toLowerCase() : value)
  );
}

function buildTrustedSets(config) {
  const source = isObject(config) ? config : {};
  const giftVariantIdsByTier = normalizeGiftVariantIdsByTier(source.gift_variant_ids_by_tier);
  return {
    protectionProducts: normalizeTrustedIdSet(source.protection_product_ids),
    protectionVariants: normalizeTrustedIdSet(source.protection_variant_ids),
    protectionSkuPrefixes: normalizeTrustedStringSet([
      PROTECTION_SKU_PREFIX,
      ...(Array.isArray(source.protection_sku_prefixes) ? source.protection_sku_prefixes : []),
    ]),
    legacyProtectionProducts: normalizeTrustedIdSet(source.legacy_protection_product_ids),
    legacyProtectionVariants: normalizeTrustedIdSet(source.legacy_protection_variant_ids),
    legacyProtectionSkus: normalizeTrustedStringSet(source.legacy_protection_skus, { lowercase: true }),
    legacyProtectionHandles: normalizeTrustedStringSet(source.legacy_protection_handles, { lowercase: true }),
    giftProducts: normalizeTrustedIdSet(source.gift_product_ids),
    giftVariants: normalizeTrustedIdSet([
      ...(Array.isArray(source.gift_variant_ids) ? source.gift_variant_ids : []),
      ...Object.values(giftVariantIdsByTier),
    ]),
    serviceProducts: normalizeTrustedIdSet(source.service_product_ids),
    serviceVariants: normalizeTrustedIdSet(source.service_variant_ids),
    excludedProducts: normalizeTrustedIdSet(source.excluded_product_ids),
    excludedVariants: normalizeTrustedIdSet(source.excluded_variant_ids),
    nonProtectableProducts: normalizeTrustedIdSet(source.non_protectable_product_ids),
    nonProtectableVariants: normalizeTrustedIdSet(source.non_protectable_variant_ids),
  };
}

function trustedMatch(productId, variantId, productSet, variantSet) {
  return (productId !== null && productSet.has(productId)) || (variantId !== null && variantSet.has(variantId));
}

function indicativePreorderFromProperties(properties) {
  for (const [key, value] of Object.entries(properties)) {
    if (/pre[-_ ]?order|précommande/i.test(`${key} ${String(value)}`)) return true;
  }
  return false;
}

function getTrustedLineClassification(context, key, productId) {
  const byLine = isObject(context?.trusted_line_classifications)
    ? context.trusted_line_classifications[key]
    : null;
  const byProduct = isObject(context?.trusted_product_classifications) && productId !== null
    ? context.trusted_product_classifications[productId]
    : null;
  return {
    ...(isObject(byProduct) ? byProduct : {}),
    ...(isObject(byLine) ? byLine : {}),
  };
}

function normalizeLine(item, index, context, trustedSets) {
  const quantity = nonNegativeInteger(item?.quantity, 0);
  const key = String(item?.key ?? item?.id ?? `line-${index + 1}`);
  const productId = normalizeId(item?.product_id ?? item?.product?.id);
  const variantId = normalizeId(item?.variant_id ?? item?.variant?.id ?? item?.merchandise_id);
  const allocations = normalizeAllocations(item);

  const explicitOriginalUnit = integerOrNull(item?.original_price ?? item?.price);
  const explicitFinalUnit = integerOrNull(item?.final_price ?? item?.discounted_price);
  let originalLinePrice = integerOrNull(item?.original_line_price);
  let finalLinePrice = integerOrNull(item?.final_line_price);

  if (originalLinePrice === null && explicitOriginalUnit !== null) {
    originalLinePrice = multiplyMoney(explicitOriginalUnit, quantity);
  }
  if (finalLinePrice === null && explicitFinalUnit !== null) {
    finalLinePrice = multiplyMoney(explicitFinalUnit, quantity);
  }
  if (finalLinePrice === null) {
    finalLinePrice = decimalMoneyStringToMinorUnits(item?.cost?.totalAmount?.amount, item?.cost?.totalAmount?.currencyCode);
  }
  if (finalLinePrice === null) finalLinePrice = 0;
  if (originalLinePrice === null) originalLinePrice = finalLinePrice;

  originalLinePrice = Math.max(0, originalLinePrice);
  finalLinePrice = Math.max(0, finalLinePrice);
  const originalUnitPrice = explicitOriginalUnit ?? unitPriceFromLine(originalLinePrice, quantity);
  const finalUnitPrice = explicitFinalUnit ?? unitPriceFromLine(finalLinePrice, quantity);
  const allocationTotal = sumIntegers(allocations.map((allocation) => allocation.amount));
  const nativeLineDiscount = integerOrNull(item?.line_level_total_discount);
  const lineDiscountTotal = Math.max(
    0,
    nativeLineDiscount ?? (allocationTotal > 0 ? allocationTotal : originalLinePrice - finalLinePrice)
  );

  const properties = normalizeProperties(item?.properties);
  const trusted = getTrustedLineClassification(context, key, productId);
  const sku = String(item?.sku ?? trusted.sku ?? '').trim();
  const productHandle = String(
    item?.product_handle
      ?? item?.handle
      ?? item?.product?.handle
      ?? trusted.product_handle
      ?? ''
  ).trim();
  const hasProtectionProperty = String(properties[PROTECTION_LINE_PROPERTY] ?? '').toLowerCase() === 'true';
  const hasProtectionSku = [...trustedSets.protectionSkuPrefixes].some((prefix) => sku.startsWith(prefix));
  const isProtection = trustedMatch(
    productId,
    variantId,
    trustedSets.protectionProducts,
    trustedSets.protectionVariants
  ) || hasProtectionProperty || hasProtectionSku;
  const isLegacyProtection = trusted.legacy_protection === true
    || trustedMatch(
      productId,
      variantId,
      trustedSets.legacyProtectionProducts,
      trustedSets.legacyProtectionVariants
    )
    || trustedSets.legacyProtectionSkus.has(sku.toLowerCase())
    || trustedSets.legacyProtectionHandles.has(productHandle.toLowerCase());
  const isGift = trustedMatch(productId, variantId, trustedSets.giftProducts, trustedSets.giftVariants);
  const isService = trustedMatch(productId, variantId, trustedSets.serviceProducts, trustedSets.serviceVariants);
  const isExplicitlyExcluded = trustedMatch(
    productId,
    variantId,
    trustedSets.excludedProducts,
    trustedSets.excludedVariants
  );
  const isNonProtectable = trustedMatch(
    productId,
    variantId,
    trustedSets.nonProtectableProducts,
    trustedSets.nonProtectableVariants
  );
  const isGiftCard = item?.gift_card === true;
  const isFree = finalLinePrice === 0;
  const isBundle = (Array.isArray(item?.item_components) && item.item_components.length > 0)
    || Boolean(item?.parent_relationship?.parent);

  const indicativePreorder = indicativePreorderFromProperties(properties);
  const hasTrustedPreorder = typeof trusted.preorder === 'boolean';
  const sellingPlanPreorder = item?.selling_plan_allocation != null;
  const isPreorder = hasTrustedPreorder ? trusted.preorder : sellingPlanPreorder || indicativePreorder;
  const preorderSource = hasTrustedPreorder
    ? 'trusted_liquid'
    : sellingPlanPreorder
      ? 'shopify_selling_plan'
      : indicativePreorder
        ? 'indicative_line_property'
        : 'not_detected';
  const hasTrustedLimited = typeof trusted.limited === 'boolean';
  const isLimited = trusted.limited === true;

  let sharedExclusionReason = null;
  if (isProtection) sharedExclusionReason = 'protection_line';
  else if (isLegacyProtection) sharedExclusionReason = 'legacy_protection_line';
  else if (isGift) sharedExclusionReason = 'gift_line';
  else if (isGiftCard) sharedExclusionReason = 'shopify_gift_card';
  else if (isFree) sharedExclusionReason = 'free_line';
  else if (isService) sharedExclusionReason = 'service_or_fee';
  else if (isExplicitlyExcluded) sharedExclusionReason = 'trusted_explicit_exclusion';

  const protectionExclusionReason = sharedExclusionReason || (isNonProtectable ? 'trusted_non_protectable' : null);
  const giftExclusionReason = sharedExclusionReason;
  const protectionEligible = protectionExclusionReason === null;
  const commercialEligible = giftExclusionReason === null;
  const giftEligible = commercialEligible;
  const ordinary = !isProtection
    && !isLegacyProtection
    && !isGift
    && !isGiftCard
    && !isService
    && !isExplicitlyExcluded;

  return Object.freeze({
    key,
    product_id: productId,
    variant_id: variantId,
    sku,
    product_handle: productHandle,
    quantity,
    original_unit_price: originalUnitPrice,
    final_unit_price: finalUnitPrice,
    original_line_price: originalLinePrice,
    final_line_price: finalLinePrice,
    discount_allocations: Object.freeze(allocations),
    line_discount_total: lineDiscountTotal,
    properties,
    parent_key: normalizeId(item?.parent_relationship?.parent?.key),
    classification: Object.freeze({
      preorder: isPreorder,
      preorder_source: preorderSource,
      limited: isLimited,
      limited_source: hasTrustedLimited ? 'trusted_liquid' : 'not_available',
      bundle: isBundle,
      ordinary,
      protection: isProtection,
      legacy_protection: isLegacyProtection,
      gift: isGift,
      gift_card: isGiftCard,
      service_or_fee: isService,
      excluded: !protectionEligible || !giftEligible,
      exclusion_reason: protectionExclusionReason || giftExclusionReason,
      protection_eligible: protectionEligible,
      protection_exclusion_reason: protectionExclusionReason,
      commercial_eligible: commercialEligible,
      commercial_exclusion_reason: giftExclusionReason,
      gift_eligible: giftEligible,
      gift_exclusion_reason: giftExclusionReason,
    }),
  });
}

function standardizedTotalPrice(source, currency) {
  return decimalMoneyStringToMinorUnits(source?.cost?.totalAmount?.amount, source?.cost?.totalAmount?.currencyCode || currency);
}

function normalizeCart(cartInput, contextInput = {}) {
  const envelope = isObject(cartInput) && isObject(cartInput.cart) ? cartInput : null;
  const source = envelope ? envelope.cart : isObject(cartInput) ? cartInput : {};
  const context = {
    ...(isObject(envelope) ? envelope : {}),
    ...(isObject(contextInput) ? contextInput : {}),
  };
  delete context.cart;

  const trustedSets = buildTrustedSets(context.trusted_config);
  const sourceItems = Array.isArray(source.items) ? source.items : Array.isArray(source.lines) ? source.lines : [];
  const lines = sourceItems.map((item, index) => normalizeLine(item, index, context, trustedSets));
  const computedOriginalTotal = sumIntegers(lines.map((line) => line.original_line_price));
  const computedItemsSubtotal = sumIntegers(lines.map((line) => line.final_line_price));
  const nativeOriginalTotal = integerOrNull(source.original_total_price);
  const nativeItemsSubtotal = integerOrNull(source.items_subtotal_price);
  const nativeDiscountTotal = integerOrNull(source.total_discount);
  const nativeFinalTotal = integerOrNull(source.total_price);
  const standardizedFinalTotal = standardizedTotalPrice(source, source.currency);
  const originalTotalPrice = Math.max(0, nativeOriginalTotal ?? computedOriginalTotal);
  const itemsSubtotalPrice = Math.max(0, nativeItemsSubtotal ?? computedItemsSubtotal);
  const totalPrice = Math.max(0, nativeFinalTotal ?? standardizedFinalTotal ?? computedItemsSubtotal);
  const totalDiscount = Math.max(0, nativeDiscountTotal ?? originalTotalPrice - totalPrice);
  const itemCount = nonNegativeInteger(
    source.item_count ?? source.totalQuantity,
    sumIntegers(lines.map((line) => line.quantity))
  );
  const currency = String(
    source.currency || source.currencyCode || source.cost?.totalAmount?.currencyCode || context.currency || 'EUR'
  ).toUpperCase();

  return Object.freeze({
    currency,
    locale: String(context.locale || 'fr'),
    item_count: itemCount,
    line_count: lines.length,
    is_empty: lines.length === 0 || itemCount === 0,
    lines: Object.freeze(lines),
    totals: Object.freeze({
      original_total_price: originalTotalPrice,
      items_subtotal_price: itemsSubtotalPrice,
      total_discount: totalDiscount,
      total_price: totalPrice,
      native_received: Object.freeze({
        original_total_price: nativeOriginalTotal,
        items_subtotal_price: nativeItemsSubtotal,
        total_discount: nativeDiscountTotal,
        total_price: nativeFinalTotal,
      }),
    }),
  });
}

function calculateCartContract(cartInput, contextInput = {}) {
  const cart = normalizeCart(cartInput, contextInput);
  const protectionEligibleSubtotal = sumIntegers(
    cart.lines
      .filter((line) => line.classification.protection_eligible)
      .map((line) => line.final_line_price)
  );
  const commercialEligibleSubtotal = sumIntegers(
    cart.lines.filter((line) => line.classification.commercial_eligible).map((line) => line.final_line_price)
  );
  const giftEligibleSubtotal = commercialEligibleSubtotal;
  const protectionQuote = calculateProtectionQuote({
    protection_eligible_subtotal: protectionEligibleSubtotal,
    currency: cart.currency,
  });
  const giftThresholds = resolvedGiftThresholds(contextInput?.gift_thresholds);
  const giftVariantIdsByTier = isObject(contextInput?.trusted_config)
    ? contextInput.trusted_config.gift_variant_ids_by_tier
    : null;
  const giftState = calculateGiftState({
    gift_eligible_subtotal: giftEligibleSubtotal,
    currency: cart.currency,
    gift_thresholds: giftThresholds,
    gift_variant_ids_by_tier: giftVariantIdsByTier,
  });
  const currentThresholds = giftThresholds[cart.currency] || null;
  const giftTiersSupported = giftState.supported;
  const reachedTierId = giftState.tier;
  const nextTierId = reachedTierId === null
    ? 'bonus'
    : reachedTierId === 'bonus'
      ? 'premium'
      : reachedTierId === 'premium'
        ? 'collector'
        : null;
  const remainingByTier = currentThresholds ? Object.fromEntries(
    GIFT_TIER_IDS.map((tier) => [tier, Math.max(0, currentThresholds[tier] - giftEligibleSubtotal)])
  ) : null;

  const ordinaryLines = cart.lines.filter((line) => line.classification.ordinary);
  const preorderLines = ordinaryLines.filter((line) => line.classification.preorder);
  const nonPreorderLines = ordinaryLines.filter((line) => !line.classification.preorder);

  return deepFreeze({
    schema_version: CONTRACT_VERSION,
    currency: cart.currency,
    locale: cart.locale,
    item_count: cart.item_count,
    line_count: cart.line_count,
    is_empty: cart.is_empty,
    shopify_totals: {
      original_total_price: cart.totals.original_total_price,
      items_subtotal_price: cart.totals.items_subtotal_price,
      total_discount: cart.totals.total_discount,
      total_price: cart.totals.total_price,
      native_received: cart.totals.native_received,
    },
    lines: cart.lines,
    commercial_eligible_subtotal: commercialEligibleSubtotal,
    protection_eligible_subtotal: protectionEligibleSubtotal,
    protection_quote: protectionQuote,
    gift_eligible_subtotal: giftEligibleSubtotal,
    gift_state: giftState,
    gift_tiers: {
      supported: giftTiersSupported,
      threshold_currency: 'EUR',
      thresholds: currentThresholds,
      remaining: remainingByTier,
      reached: reachedTierId,
      next: giftTiersSupported ? nextTierId : null,
      remaining_to_next: giftState.remaining,
    },
    indicators: {
      protection_eligible_amount: protectionEligibleSubtotal,
      has_preorder: preorderLines.length > 0,
      has_mixed_cart: preorderLines.length > 0 && nonPreorderLines.length > 0,
      has_excluded_line: cart.lines.some((line) => line.classification.excluded),
    },
  });
}

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  Object.freeze(value);
  for (const child of Object.values(value)) deepFreeze(child);
  return value;
}

let currentContract = null;
let currentContext = {};
let publicationSequence = 0;
let lastSignature = null;
let latestMutationToken = 0;
const handledEvents = new WeakSet();

function getCurrentCartContract() {
  return currentContract;
}

function mergeContext(nextContext) {
  if (!isObject(nextContext)) return;
  currentContext = {
    ...currentContext,
    ...nextContext,
    trusted_config: {
      ...(isObject(currentContext.trusted_config) ? currentContext.trusted_config : {}),
      ...(isObject(nextContext.trusted_config) ? nextContext.trusted_config : {}),
    },
    trusted_line_classifications: {
      ...(isObject(currentContext.trusted_line_classifications) ? currentContext.trusted_line_classifications : {}),
      ...(isObject(nextContext.trusted_line_classifications) ? nextContext.trusted_line_classifications : {}),
    },
    trusted_product_classifications: {
      ...(isObject(currentContext.trusted_product_classifications) ? currentContext.trusted_product_classifications : {}),
      ...(isObject(nextContext.trusted_product_classifications) ? nextContext.trusted_product_classifications : {}),
    },
  };
}

function publishContract(cart, reason, context = null) {
  mergeContext(context);
  const contract = calculateCartContract(cart, currentContext);
  const signature = JSON.stringify(contract);
  if (signature === lastSignature) return currentContract;

  currentContract = contract;
  lastSignature = signature;
  publicationSequence += 1;
  mirrorContract(contract, reason, publicationSequence);
  document.dispatchEvent(
    new CustomEvent(CONTRACT_EVENT, {
      detail: Object.freeze({ contract, reason, sequence: publicationSequence }),
    })
  );
  return currentContract;
}

function mirrorContract(contract, reason, sequence) {
  let node = document.getElementById(DEBUG_DATA_ID);
  if (!node) {
    node = document.createElement('script');
    node.id = DEBUG_DATA_ID;
    node.type = 'application/json';
    document.head.appendChild(node);
  }
  node.textContent = JSON.stringify({ contract, reason, sequence });
}

function cartUrl() {
  const root = String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  return `${root}cart.js`;
}

async function fetchAjaxCart() {
  const response = await fetch(cartUrl(), {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Unable to refresh cart contract (${response.status})`);
  return response.json();
}

function cartFromEventResult(result) {
  if (Array.isArray(result?.detail?.rawCart?.items)) return result.detail.rawCart;
  if (!Array.isArray(result?.detail?.items)) return null;

  const currency = result?.cart?.cost?.totalAmount?.currencyCode || currentContract?.currency || 'EUR';
  return {
    items: result.detail.items,
    item_count: result?.cart?.totalQuantity,
    total_price: decimalMoneyStringToMinorUnits(result?.cart?.cost?.totalAmount?.amount, currency),
    currency,
  };
}

async function handleCartMutation(event) {
  if (handledEvents.has(event)) return;
  handledEvents.add(event);
  const token = ++latestMutationToken;

  try {
    const result = event?.promise ? await event.promise : null;
    if (token < latestMutationToken || result?.detail?.didError === true) return;
    const cart = cartFromEventResult(result) || (await fetchAjaxCart());
    publishContract(cart, event.type);
  } catch (error) {
    if (error?.name !== 'AbortError') console.warn('[poke-geek-cart-contract] Update skipped:', error);
  }
}

async function refreshCurrentCartContract(reason = 'manual-refresh') {
  const cart = await fetchAjaxCart();
  return publishContract(cart, reason);
}

function readInitialData() {
  const node = document.getElementById(INITIAL_DATA_ID);
  if (!node?.textContent) return null;
  try {
    const data = JSON.parse(node.textContent);
    return isObject(data) ? data : null;
  } catch (error) {
    console.warn('[poke-geek-cart-contract] Invalid initial cart JSON:', error);
    return null;
  }
}

function initializeBrowserContract() {
  const initial = readInitialData();
  if (initial?.cart) publishContract(initial.cart, 'initial-liquid', initial);
  else refreshCurrentCartContract('initial-fallback').catch((error) => {
    console.warn('[poke-geek-cart-contract] Initial refresh skipped:', error);
  });

  document.addEventListener(StandardEvents.cartLinesUpdate, handleCartMutation);
  document.addEventListener(StandardEvents.cartDiscountUpdate, handleCartMutation);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCurrentCartContract('pageshow-bfcache').catch(() => {});
  });

  window.PokeGeekCartContract = Object.freeze({
    version: CONTRACT_VERSION,
    eventName: CONTRACT_EVENT,
    calculateCartContract,
    calculateProtectionQuote,
    calculateGiftState,
    normalizeCart,
    getCurrentCartContract,
    refreshCurrentCartContract,
  });
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') initializeBrowserContract();

export {
  CONTRACT_EVENT,
  CONTRACT_VERSION,
  GIFT_TIERS,
  calculateGiftState,
  calculateCartContract,
  calculateProtectionQuote,
  decimalMoneyStringToMinorUnits,
  getCurrentCartContract,
  normalizeCart,
  refreshCurrentCartContract,
};
